import { FunctionalComponent } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";
import { Tag } from "../utils/api";

interface TagInputProps {
  selectedTags: string[];
  availableTags: Tag[];
  onChange: (tags: string[]) => void;
  onCreateTag?: (name: string) => void;
}

export const TagInput: FunctionalComponent<TagInputProps> = ({
  selectedTags,
  availableTags,
  onChange,
  onCreateTag,
}) => {
  const [input, setInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [newTagError, setNewTagError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const safeTags = Array.isArray(selectedTags) ? selectedTags : [];

  const filteredTags = availableTags.filter(
    (tag) =>
      tag.name.toLowerCase().includes(input.toLowerCase()) &&
      !safeTags.includes(tag.name)
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && input.trim()) {
      e.preventDefault();
      if (safeTags.includes(input.trim().toLowerCase())) {
        setNewTagError("Tag already selected");
        return;
      }
      if (onCreateTag) {
        onCreateTag(input.trim().toLowerCase());
      } else {
        onChange([...safeTags, input.trim().toLowerCase()]);
      }
      setInput("");
      setNewTagError("");
    } else if (e.key === "Backspace" && !input && safeTags.length > 0) {
      onChange(safeTags.slice(0, -1));
    }
  };

  const addTag = (tagName: string) => {
    if (!safeTags.includes(tagName)) {
      onChange([...safeTags, tagName]);
    }
    setInput("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeTag = (tagName: string) => {
    onChange(safeTags.filter((t) => t !== tagName));
  };

  const showCreateOption =
    input.trim() &&
    !safeTags.includes(input.trim().toLowerCase()) &&
    !availableTags.some((t) => t.name === input.trim().toLowerCase());

  return (
    <div ref={containerRef} class="relative">
      <label class="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
        Tags
      </label>

      <div class="flex flex-wrap gap-2 mb-2">
        {safeTags.map((tag) => (
          <span
            key={tag}
            class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700 transition-colors"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
      </div>

      <div class="relative">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onInput={(e) => {
            setInput((e.target as HTMLInputElement).value);
            setNewTagError("");
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder="Type to search or create tags..."
          class="w-full px-4 py-2.5 border border-stone-200 dark:border-stone-800 rounded-xl bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all shadow-sm"
        />

        {showSuggestions && (filteredTags.length > 0 || showCreateOption) && (
          <div class="absolute z-10 w-full mt-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl shadow-xl max-h-60 overflow-y-auto py-1">
            {showCreateOption && onCreateTag && (
              <button
                type="button"
                onClick={() => {
                  onCreateTag(input.trim().toLowerCase());
                  setInput("");
                }}
                class="w-full px-4 py-2.5 text-left text-sm text-primary-600 dark:text-primary-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors flex items-center gap-2 font-medium"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
                Create "{input.trim().toLowerCase()}"
              </button>
            )}
            {filteredTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => addTag(tag.name)}
                class="w-full px-4 py-2.5 text-left text-sm hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors text-stone-900 dark:text-stone-100"
              >
                {tag.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {newTagError && (
        <p class="mt-1 text-sm text-red-600 dark:text-red-400">{newTagError}</p>
      )}
    </div>
  );
};
